import React, { useState } from "react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { PhoneCall, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { TwoContactPathText } from "i18n/contactCTA";
import { fadeUp } from "../../animations/variants";
import CTAButton from "Util/CTAButton";
import { useNavigate } from "react-router-dom";

const TwoContactPath = () => {
  const lang = useSelector((state) => state.language.lang);
  const t = TwoContactPathText[lang];
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const handleFullForm = () => {
    navigate("/contact/full-form", {
      state: {
        fullName: name,
        email,
        phone,
        message,
      },
    });
  };

  return (
    <section className="relative overflow-hidden py-20 bg-[#f7f3ef]">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-reddark/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-8xl mx-auto px-0 lg:px-6">
        {/* Heading */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="inline-block text-xs uppercase tracking-[0.35em] text-textGray font-light mb-4">
            Maria Advisory
          </span>
          <h2 className="font-[Crimson] text-4xl md:text-6xl text-reddark mb-4">
            {lang === "en"
              ? "Choose Your Preferred Contact Path"
              : "Chọn Hình Thức Liên Hệ Phù Hợp"}
          </h2>
          <p className="max-w-3xl mx-auto text-textGray leading-relaxed">
            {lang === "en"
              ? "Whether you would like a complimentary strategy discussion or simply want to send an enquiry, we're here to help."
              : "Dù bạn muốn đặt lịch tư vấn miễn phí hay gửi yêu cầu trực tiếp, Maria luôn sẵn sàng hỗ trợ bạn."}
          </p>{" "}
          <div className="w-24 h-[2px] bg-reddark my-5 mx-auto" />
        </motion.div>

        <div className="relative grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Divider */}
          <div
            className="
              hidden lg:block
              absolute
              left-1/2
              top-10
              bottom-10
              w-px
              bg-gradient-to-b
              from-transparent
              via-[#d8c9bc]
              to-transparent
              -translate-x-1/2
            "
          />

          {/* LEFT CARD */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            whileHover={{ y: -8 }}
            transition={{ duration: 0.35 }}
            className="
              relative overflow-hidden
              rounded-xl
              bg-gradient-to-br
              from-reddark
              via-[#b51220]
              to-[#7f0914]
              p-3 lg:p-3
              shadow-[0_35px_90px_rgba(120,0,0,.35)]
            "
          >
            {/* Glow */}
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-black/10 blur-3xl" />

            <div className="relative z-10">
              {/* Badge */}

              {/* Header */}
              <div className="flex items-start gap-5 mt-3 mb-8">
                <div
                  className="
      relative
      w-14 h-14
      shrink-0
      rounded-2xl
      bg-white/10
      border border-white/15
      backdrop-blur-md
      flex items-center justify-center
    "
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/20 to-transparent" />

                  <PhoneCall className="relative w-6 h-6 text-white" />
                </div>

                <div>
                  <p
                    className="
        text-[11px]
        uppercase
        tracking-[0.25em]
        text-white/60
        mb-2
        font-medium
      "
                  >
                    {lang === "en" ? "Preferred Option" : "Lựa Chọn Ưu Tiên"}
                  </p>

                  <h2
                    className="
        font-[Crimson]
        text-2xl md:text-4xl
        font-semibold
        text-white
        leading-tight
      "
                  >
                    {t.title1}
                  </h2>
                </div>
              </div>

              {/* Rows */}
              <div className="space-y-3 mb-8 ">
                {t.rows.map((row, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.1,
                    }}
                    className="
                      flex items-center gap-4
                      rounded-2xl
                      bg-black/15
                      border border-white/10
                      backdrop-blur-sm
                      px-5 py-4
                    "
                  >
                    <CheckCircle2 className="w-5 h-5 text-white shrink-0" />

                    <div className="w-36 font-semibold text-white">
                      {row.label}
                    </div>

                    <div className="text-white/90">{row.value}</div>
                  </motion.div>
                ))}
              </div>

              {/* Calendly */}
              <div
                className="
                  rounded-3xl
                  border border-white/15
                  bg-white/10
                  backdrop-blur-sm
                  p-10
                  text-center
                  text-white/80
                  mb-8
                "
              >
                <div className="text-sm uppercase tracking-[0.2em] mb-3 text-white/60">
                  Calendly
                </div>

                <p>{t.calendly}</p>

                {/* Replace with Calendly widget */}
                {/* <InlineWidget url="YOUR_CALENDLY_URL" /> */}
              </div>

              {/* CTA */}

              <div className="flex justify-center pb-8">
                <CTAButton
                  icon={PhoneCall}
                  text={t.ctaBook}
                  variant="secondary"
                  className="
    mt-4
    bg-white
    text-reddark
    border border-white/20
    shadow-lg
    hover:shadow-xl
  "
                />
              </div>
            </div>
          </motion.div>

          {/* RIGHT CARD */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            whileHover={{ y: -8 }}
            transition={{ duration: 0.35 }}
            className="
              relative overflow-hidden
              rounded-xl
              bg-white
              border border-[#e5d8cc]
              p-6 lg:p-10
              shadow-[0_20px_60px_rgba(0,0,0,.08)]
            "
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-reddark via-amber to-reddark" />

            {/* Header */}
            <div className="flex items-start gap-5 mt-0 mb-8">
              <div
                className="
      relative
      w-14 h-14
      shrink-0
      rounded-2xl
      bg-white/10
      border border-white/15
      backdrop-blur-md
      flex items-center justify-center
    "
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-reddark/20 to-transparent" />

                <Mail className="w-8 h-8 text-[#8a6d56]" />
              </div>

              <div>
                <p
                  className="
        text-[11px] text-textGray
        uppercase
        tracking-[0.25em]
        mb-2
        font-medium
      "
                >
                  {t.badge2}
                </p>

                <h2
                  className="
        font-[Crimson]
        text-2xl md:text-4xl
        font-semibold
        leading-tight text-reddark
      "
                >
                  {t.title2}
                </h2>
              </div>
            </div>

            {/* Form */}
            <div className="space-y-5">
              <div>
                <label className="text-sm font-medium text-[#6d5545]">
                  {t.form.name}
                </label>

                <input
                  className="
                    w-full mt-2
                    rounded-xl
                    border border-[#e5d8cc]
                    bg-[#f7f3ef]
                    px-4 py-3
                    focus:border-reddark
                    focus:outline-none
                    transition
                  "
                  placeholder={t.form.namePlaceholder}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-[#6d5545]">
                  {t.form.email}
                </label>

                <input
                  className="
                    w-full mt-2
                    rounded-xl
                    border border-[#e5d8cc]
                    bg-[#f7f3ef]
                    px-4 py-3
                    focus:border-reddark
                    focus:outline-none
                    transition
                  "
                  placeholder={t.form.emailPlaceholder}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-[#6d5545]">
                  {t.form.phone}
                </label>

                <input
                  className="
                    w-full mt-2
                    rounded-xl
                    border border-[#e5d8cc]
                    bg-[#f7f3ef]
                    px-4 py-3
                    focus:border-reddark
                    focus:outline-none
                    transition
                  "
                  placeholder={t.form.phonePlaceholder}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-[#6d5545]">
                  {t.form.message}
                </label>

                <textarea
                  rows={5}
                  className="
                    w-full mt-2
                    rounded-xl
                    border border-[#e5d8cc]
                    bg-[#f7f3ef]
                    px-4 py-3
                    focus:border-reddark
                    focus:outline-none
                    transition
                  "
                  placeholder={t.form.messagePlaceholder}
                />
              </div>

              <div className="flex justify-center pb-8">
                <CTAButton
                  icon={Mail}
                  text={t.form.button}
                  onClick={handleFullForm}
                  variant="secondary"
                  className="
    mt-4
                  bg-[#140202]
    text-white
    border border-white/20
    shadow-lg
    hover:shadow-xl
  "
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TwoContactPath;
